import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Spinner } from "../../components/ui/spinner";
import { Card } from "../../components/ui/card";
import { callModelEndpoint } from "../../openai";
import { ClientAPIKey } from "@/openai";

const modelOptions = [
  {
    label: "ART-S (OpenPipe)",
    model: "arcticfly/summarize-agent-015-qa-41",
    clientKey: ClientAPIKey.TOGETHER,
  },
  {
    label: "Claude 4 Sonnet (Anthropic)",
    model: "anthropic/claude-sonnet-4",
    clientKey: ClientAPIKey.OPENROUTER,
  },
  {
    label: "GPT-4o (OpenAI)",
    model: "gpt-4o",
    clientKey: ClientAPIKey.OPENAI,
  },
];

export const SummaryDemo = () => {
  const [inputText, setInputText] = useState("");
  const [summaries, setSummaries] = useState<Record<string, string>>({});
  const [selectedModel, setSelectedModel] = useState<string>(
    modelOptions[0].model
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputText.trim()) {
      setError("Please enter some text to summarize.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const responses = await Promise.all(
        modelOptions.map((option) => {
          return callModelEndpoint({
            model: option.model,
            messages: [
              {
                role: "user",
                content: `You are a specialized AI assistant that generates concise, informative summaries for documents.
      
Here is a document: ${inputText}

Generate a summary of the document. Aim for about 300 words.`,
              },
            ],
            temperature: 0,
            max_tokens: 500,
            clientKey: option.clientKey,
          }).then((res) => ({
            model: option.model,
            content: res.choices[0].message.content,
          }));
        })
      );

      const newSummaries: Record<string, string> = {};
      responses.forEach(({ model, content }) => {
        newSummaries[model] = content;
      });

      setSummaries(newSummaries);
      setSelectedModel(modelOptions[0].model);
    } catch (err) {
      console.error("Error generating summaries:", err);
      setError("Failed to generate summaries. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <Card className="p-6 bg-white dark:bg-gray-800 shadow-md rounded-lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="input-text"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Enter text to summarize
            </label>
            <Textarea
              id="input-text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Paste your text here..."
              className="min-h-[200px] w-full"
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading || !inputText.trim()}
            className="w-full"
          >
            {isLoading ? <Spinner className="mr-2" /> : null}
            {isLoading ? "Generating Summaries..." : "Generate Summary"}
          </Button>

          {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
        </form>

        {Object.keys(summaries).length > 0 && (
          <div className="mt-6">
            {/* Model Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
              <nav
                className="-mb-px flex space-x-4 overflow-x-auto"
                aria-label="Tabs"
              >
                {modelOptions.map((option) => (
                  <button
                    key={option.model}
                    onClick={() => setSelectedModel(option.model)}
                    className={`whitespace-nowrap py-2 px-4 border-b-2 font-medium text-sm cursor-pointer focus:outline-none ${
                      selectedModel === option.model
                        ? "border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                        : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Selected Summary */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md whitespace-pre-wrap">
              <p>Word Count: {summaries[selectedModel].split(" ").length}</p>

              {summaries[selectedModel]}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default SummaryDemo;
